import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Spinner, Badge, Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_URL } from "../../utils/constant";
import { 
    faGlobe, 
    faNetworkWired, 
    faSkull, 
    faSearch, 
    faTimes, 
    faInfoCircle,
    faBuilding,
    faBug,
    faTag,
    faClock
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

const ThreatActorList = () => {
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [threatActors, setThreatActors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [cursor, setCursor] = useState(null);
    const [startCursor, setStartCursor] = useState(null);
    const [cursorHistory, setCursorHistory] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [hasPrevious, setHasPrevious] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [orderBy, setOrderBy] = useState('name');
    const [orderMode, setOrderMode] = useState('asc');

    const fetchThreatActors = async (cursorParam = null, isPrevious = false) => {
        if (!cursorParam) {
        setLoading(true);
        } else {
            setIsLoadingMore(true);
        }
        
        try {
            let url = `${API_URL}/threat-intel/threat-actors?limit=25`;
            
            if (cursorParam) {
                url += `&cursor=${encodeURIComponent(cursorParam)}`;
            }
            
            if (searchTerm && searchTerm.trim()) {
                url += `&search=${encodeURIComponent(searchTerm.trim())}`;
            }
            
            if (orderBy) {
                url += `&orderBy=${orderBy}`;
            }
            
            if (orderMode) {
                url += `&orderMode=${orderMode}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.error) {
                throw new Error(data.userMessage || 'Failed to fetch threat actors');
            }

            const results = data.data?.results || [];
            setThreatActors(results);

            // Update cursor and pagination info
            const pagination = data.data?.pagination;
            if (pagination) {
                setCursor(pagination.endCursor);
                setStartCursor(pagination.startCursor);
                setHasMore(pagination.hasNextPage || false);
                setHasPrevious(pagination.hasPreviousPage || false);
                setTotalCount(pagination.total || 0);
                
                // Update cursor history for navigation (only when going forward)
                if (!isPrevious && pagination.endCursor) {
                    setCursorHistory(prev => {
                        if (prev.length === 0 || prev[prev.length - 1] !== pagination.endCursor) {
                            const newHistory = [...prev, pagination.endCursor];
                            return newHistory.slice(-50);
                        }
                        return prev;
                    });
                }
            } else {
                setHasMore(results.length === 25);
                setHasPrevious(false);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchThreatActors(null, false);
        setCurrentPage(1);
        setCursorHistory([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch when search term, orderBy, or orderMode changes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setCurrentPage(1);
            setCursorHistory([]);
            fetchThreatActors(null, false);
        }, 500); // Debounce search by 500ms

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, orderBy, orderMode]);

    const handleNextPage = () => {
        if (cursor && hasMore) {
            setCurrentPage(prev => prev + 1);
            fetchThreatActors(cursor, false);
        }
    };

    const handlePreviousPage = () => {
        if (cursorHistory.length > 1) {
            const newHistory = [...cursorHistory];
            newHistory.pop();
            const previousCursor = newHistory.length > 0 ? newHistory[newHistory.length - 1] : null;
            
            setCursorHistory(newHistory);
            setCurrentPage(prev => Math.max(1, prev - 1));
            
            if (previousCursor && previousCursor !== startCursor) {
                fetchThreatActors(previousCursor, true);
            } else {
                setCursorHistory([]);
                setCurrentPage(1);
                fetchThreatActors(null, true);
            }
        } else {
            setCursorHistory([]);
            setCurrentPage(1);
            fetchThreatActors(null, true);
        }
    };

    const handleFirstPage = () => {
        setCurrentPage(1);
        setCursorHistory([]);
        fetchThreatActors(null, false);
    };

    const handleViewDetails = (actor, event) => {
        // Navigate to detail page
        if (event && (event.ctrlKey || event.metaKey)) {
            // Open in new tab if Ctrl/Cmd is held
            window.open(`/threat-intel/threat-actors/${actor.id}`, '_blank');
        } else {
            history.push(`/threat-intel/threat-actors/${actor.id}`);
        }
    };


    const getConfidenceColor = (confidence) => {
        if (confidence >= 80) return '#dc3545';
        if (confidence >= 50) return '#ffc107';
        if (confidence >= 20) return '#0d6efd';
        return '#6c757d';
    };

    const getConfidenceLabel = (confidence) => {
        if (confidence >= 80) return 'High';
        if (confidence >= 50) return 'Medium';
        if (confidence >= 20) return 'Low';
        return 'Unknown';
    };

    const getResourceLevelColor = (level) => {
        switch (level?.toLowerCase()) {
            case 'government': return 'danger';
            case 'individual': return 'warning';
            case 'organization': return 'info';
            default: return 'secondary';
        }
    };

    const getMotivationColor = (motivation) => {
        switch (motivation?.toLowerCase()) {
            case 'organizational-gain': return 'primary';
            case 'personal-gain': return 'warning';
            case 'ideology': return 'danger';
            case 'dominance': return 'dark';
            default: return 'secondary';
        }
    };

    const extractCountries = (targetsCountries) => {
        if (!targetsCountries?.edges || targetsCountries.edges.length === 0) return [];
        return targetsCountries.edges.map(edge => edge.node?.to?.name).filter(Boolean);
    };

    const extractSectors = (targetsSectors) => {
        if (!targetsSectors?.edges || targetsSectors.edges.length === 0) return [];
        return targetsSectors.edges.map(edge => edge.node?.to?.name).filter(Boolean);
    };

    const extractMalware = (usesMalware) => {
        if (!usesMalware?.edges || usesMalware.edges.length === 0) return [];
        return usesMalware.edges.map(edge => ({
            name: edge.node?.to?.name,
            types: edge.node?.to?.malware_types
        })).filter(item => item.name);
    };

    return (
        <div className="container-fluid">
            <div className="content-wrapper">
                <div className="block-header mb-4">
                    <h2 className="welcome-title">Threat Actor Library</h2>
                    <p className="welcome-textSmall" style={{ color: 'white' }}>Advanced Persistent Threats (APT) and Threat Group Profiles</p>
                </div>

                {error && (
                    <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
                        {error}
                    </Alert>
                )}

                {/* Search and Sort Section */}
                <Card className="mb-4 shadow-sm" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                    <Card.Body className="p-4">
                        <div className="row g-4">
                            <div className="col-12 col-md-6 col-lg-5">
                                <label className="form-label small text-muted mb-2 fw-semibold d-flex align-items-center">
                                    <FontAwesomeIcon icon={faSearch} className="me-1" />
                                    Search Threat Actors
                                </label>
                                <div className="input-group" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #dee2e6', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                    <span className="input-group-text bg-white border-0" style={{ paddingLeft: '16px', paddingRight: '12px' }}>
                                        <FontAwesomeIcon icon={faSearch} className="text-muted" style={{ fontSize: '0.9rem' }} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-0"
                                        placeholder="Search by name, aliases, or description..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ fontSize: '0.95rem', padding: '12px 16px', border: 'none' }}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="input-group-text bg-white border-0"
                                            onClick={() => setSearchTerm('')}
                                            style={{ cursor: 'pointer', color: '#6c757d', paddingRight: '16px', paddingLeft: '8px' }}
                                            title="Clear search"
                                        >
                                            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.9rem' }} />
                                        </button>
                                    )}
                                </div>
                                {searchTerm && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Searching for: <strong className="text-primary">{searchTerm}</strong>
                                        </small>
                                    </div>
                                )}
                            </div>
                            <div className="col-6 col-md-3 col-lg-3">
                                <label className="form-label small text-muted mb-2 fw-semibold">
                                    Sort By
                                </label>
                                <select
                                    className="form-select"
                                    value={orderBy}
                                    onChange={(e) => setOrderBy(e.target.value)}
                                    style={{ 
                                        borderRadius: '8px', 
                                        padding: '12px 16px',
                                        border: '1px solid #dee2e6',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    <option value="name">Name</option>
                                    <option value="created">Created Date</option>
                                    <option value="modified">Modified Date</option>
                                    <option value="confidence">Confidence</option>
                                </select>
                            </div>
                            <div className="col-6 col-md-3 col-lg-2">
                                <label className="form-label small text-muted mb-2 fw-semibold">
                                    Order
                                </label>
                                <select
                                    className="form-select"
                                    value={orderMode}
                                    onChange={(e) => setOrderMode(e.target.value)}
                                    style={{ 
                                        borderRadius: '8px', 
                                        padding: '12px 16px',
                                        border: '1px solid #dee2e6',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    <option value="asc">Ascending</option>
                                    <option value="desc">Descending</option>
                                </select>
                            </div>
                            <div className="col-12 col-md-12 col-lg-2 d-flex align-items-end">
                                {threatActors.length > 0 && (
                                    <div className="w-100">
                                        <label className="form-label small text-muted mb-2 fw-semibold" style={{ color: 'white' }}>
                                            Results
                                        </label>
                                        <div className="p-2 bg-light rounded text-center" style={{ border: '1px solid #dee2e6' }}>
                                            <strong className="text-primary">{threatActors.length}</strong>
                                            {totalCount > 0 && (
                                                <small className="text-muted d-block">of {totalCount}</small>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {loading ? (
                    <div className="text-center my-5 py-5">
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <p className="mt-3 text-muted">Loading threat actors...</p>
                    </div>
                ) : (
                    <>
                        <Card className="shadow-sm" style={{ border: '1px solid #e9ecef', borderRadius: '12px', overflow: 'hidden' }}>
                            <Card.Header className='d-flex align-items-center justify-content-between' style={{ 
                                background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', 
                                color: 'white',
                                padding: '20px 24px',
                                borderBottom: 'none'
                            }}>
                                <div className="d-flex align-items-center">
                                    <div style={{ 
                                        width: '48px', 
                                        height: '48px', 
                                        borderRadius: '12px', 
                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '12px'
                                    }}>
                                        <FontAwesomeIcon icon={faSkull} style={{ fontSize: '1.5rem' }} />
                                    </div>
                                    <div>
                                        <h4 className='mb-0 fw-bold' style={{ color: 'white' }}>Threat Actor Profiles</h4>
                                        <small style={{ opacity: 0.9 }}>Threat Intelligence Library</small>
                                    </div>
                                </div>
                                {threatActors.length > 0 && (
                                    <Badge bg="light" text="dark" className="px-3 py-2 fw-semibold" style={{ fontSize: '0.9rem', borderRadius: '8px' }}>
                                        {threatActors.length} {threatActors.length === 1 ? 'Actor' : 'Actors'}
                                    </Badge>
                                )}
                        </Card.Header>
                            <Card.Body className="p-4">
                                {threatActors.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-3" style={{ fontSize: '4rem', opacity: 0.5 }} />
                                        <h5 className="text-muted mb-2">No threat actors found</h5>
                                        <p className="text-muted">
                                            {searchTerm ? 'Try adjusting your search terms or clear the search to see all threat actors.' : 'No threat actors available at this time.'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="row g-4" style={{ margin: '0 -8px' }}>
                                        {threatActors.map((actor) => {
                                            const confidence = actor.confidence || 0;
                                            const confidenceColor = getConfidenceColor(confidence);
                                            const countries = extractCountries(actor.targetsCountries);
                                            const sectors = extractSectors(actor.targetsSectors);
                                            const malware = extractMalware(actor.usesMalware);
                                            const resourceLevel = actor.resource_level;
                                            const primaryMotivation = actor.primary_motivation;
                                            
                                            return (
                                                <div key={actor.id} className="col-12 col-md-6 col-lg-4" style={{ padding: '0 8px', marginBottom: '16px' }}>
                                                    <Card 
                                                        className="h-100 shadow-sm" 
                                                        style={{ 
                                                            border: '1px solid #e9ecef', 
                                                            borderRadius: '16px',
                                                            transition: 'all 0.3s ease',
                                                            cursor: 'pointer',
                                                            overflow: 'hidden',
                                                            height: '100%',
                                                            display: 'flex',
                                                            flexDirection: 'column'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(-6px)';
                                                            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.12)';
                                                            e.currentTarget.style.borderColor = '#dc3545';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                                                            e.currentTarget.style.borderColor = '#e9ecef';
                                                        }}
                                                        onClick={() => handleViewDetails(actor)}
                                                    >
                                                        <Card.Body className="p-4" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                            {/* Header with Name and Confidence */}
                                                            <div className="d-flex align-items-start justify-content-between mb-3">
                                                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                                    <h5 className="fw-bold mb-2" style={{ 
                                                                        color: '#212529', 
                                                                        fontSize: '1.15rem',
                                                                        lineHeight: '1.3',
                                                                        wordBreak: 'break-word'
                                                                    }}>
                                                                        {actor?.name}
                                                                    </h5>
                                                                    {actor?.aliases && actor?.aliases?.length > 0 && (
                                                                        <div className="mb-2">
                                                                            <small className="text-muted d-flex align-items-center flex-wrap">
                                                                                <FontAwesomeIcon icon={faTag} className="me-1" style={{ fontSize: '0.75rem' }} />
                                                                                <span style={{ fontSize: '0.8rem' }}>
                                                                                    {actor.aliases.slice(0, 2).join(', ')}
                                                                                    {actor.aliases.length > 2 && ` +${actor.aliases.length - 2} more`}
                                                                                </span>
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {/* <div 
                                                                    className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                                                                    style={{ 
                                                                        width: '56px', 
                                                                        height: '56px', 
                                                                        backgroundColor: `${confidenceColor}15`,
                                                                        color: confidenceColor,
                                                                        fontWeight: 'bold',
                                                                        fontSize: '0.85rem',
                                                                        border: `2.5px solid ${confidenceColor}40`,
                                                                        marginLeft: '12px'
                                                                    }}
                                                                    title={`Confidence: ${confidence}% (${getConfidenceLabel(confidence)})`}
                                                                >
                                                                    {confidence}%
                                                                </div> */}
                                                            </div>

                                                            {/* Description */}
                                                            {actor.description && (
                                                                <p className="text-muted small mb-3" style={{ 
                                                                    lineHeight: '1.6',
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 3,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden',
                                                                    fontSize: '0.85rem',
                                                                    flexShrink: 0
                                                                }}>
                                                                    {actor.description.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')}
                                                                </p>
                                                            )}

                                                            {/* Resource Level and Motivation */}
                                                            <div className="mb-3 d-flex flex-wrap gap-2" style={{ flexShrink: 0 }}>
                                                                {resourceLevel && (
                                                                    <Badge bg={getResourceLevelColor(resourceLevel)} style={{ fontSize: '0.7rem', padding: '4px 10px' }}>
                                                                        <FontAwesomeIcon icon={faBuilding} className="me-1" />
                                                                        {resourceLevel}
                                                                    </Badge>
                                                                )}
                                                                {primaryMotivation && (
                                                                    <Badge bg={getMotivationColor(primaryMotivation)} style={{ fontSize: '0.7rem', padding: '4px 10px', color: 'white' }}>
                                                                        {primaryMotivation.replace(/-/g, ' ')}
                                                                    </Badge>
                                                                )}
                                                            </div>

                                                            {/* Tags/Labels */}
                                                            {actor.objectLabel && actor.objectLabel.length > 0 && (
                                                                <div className="mb-3" style={{ flexShrink: 0 }}>
                                                                    <div className="d-flex flex-wrap gap-1">
                                                                        {actor.objectLabel.slice(0, 5).map((label, idx) => (
                                                                            <Badge 
                                                                                key={idx}
                                                                                style={{ 
                                                                                    backgroundColor: label.color || '#6c757d',
                                                                                    color: '#FFFFFF',
                                                                                    fontSize: '0.7rem',
                                                                                    padding: '3px 8px',
                                                                                    fontWeight: '500',
                                                                                    borderRadius: '4px'
                                                                                }}
                                                                                title={label.value}
                                                                            >
                                                                                {label.value}
                                                                            </Badge>
                                                                        ))}
                                                                        {actor.objectLabel.length > 5 && (
                                                                            <Badge bg="secondary" style={{ fontSize: '0.7rem', padding: '3px 8px', borderRadius: '4px' }}>
                                                                                +{actor.objectLabel.length - 5}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Footer with Stats */}
                                                            <div className="border-top pt-3 mt-auto" style={{ flexShrink: 0 }}>
                                                                <div className="row g-2">
                                                                    {countries.length > 0 && (
                                                                        <div className="col-12">
                                                                            <div className="d-flex align-items-center small text-muted mb-1">
                                                                                <FontAwesomeIcon icon={faGlobe} className="me-2" style={{ fontSize: '0.8rem', color: '#0d6efd' }} />
                                                                                <strong className="me-1" style={{ fontSize: '0.8rem' }}>Countries:</strong>
                                                                                <span style={{ fontSize: '0.8rem' }}>
                                                                                    {countries.slice(0, 3).join(', ')}
                                                                                    {countries.length > 3 && <span className="text-primary"> +{countries.length - 3}</span>}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {sectors.length > 0 && (
                                                                        <div className="col-12">
                                                                            <div className="d-flex align-items-center small text-muted mb-1">
                                                                                <FontAwesomeIcon icon={faNetworkWired} className="me-2" style={{ fontSize: '0.8rem', color: '#ffc107' }} />
                                                                                <strong className="me-1" style={{ fontSize: '0.8rem' }}>Sectors:</strong>
                                                                                <span style={{ fontSize: '0.8rem' }}>
                                                                                    {sectors.slice(0, 2).join(', ')}
                                                                                    {sectors.length > 2 && <span className="text-primary"> +{sectors.length - 2}</span>}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {malware.length > 0 && (
                                                                        <div className="col-12">
                                                                            <div className="d-flex align-items-center small text-muted">
                                                                                <FontAwesomeIcon icon={faBug} className="me-2" style={{ fontSize: '0.8rem', color: '#dc3545' }} />
                                                                                <strong className="me-1" style={{ fontSize: '0.8rem' }}>Malware:</strong>
                                                                                <span style={{ fontSize: '0.8rem' }} className="text-danger fw-semibold">
                                                                                    {malware.length} {malware.length === 1 ? 'type' : 'types'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {actor.created && (
                                                                        <div className="col-12">
                                                                            <div className="d-flex align-items-center small text-muted">
                                                                                <FontAwesomeIcon icon={faClock} className="me-2" style={{ fontSize: '0.75rem' }} />
                                                                                <span style={{ fontSize: '0.75rem' }}>
                                                                                    Created: {moment(actor.created).format('MMM YYYY')}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Pagination Controls */}
                                {threatActors.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center py-3 border-top bg-light px-3 mt-4" style={{ borderRadius: '8px' }}>
                                        <div className="text-muted small">
                                            Showing {threatActors.length} actors
                                            {totalCount > 0 && ` of ${totalCount} total`}
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <button
                                                className="btn btn-sm btn-outline-secondary me-2"
                                                onClick={handleFirstPage}
                                                disabled={currentPage === 1 || loading || isLoadingMore}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                First
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-secondary me-2"
                                                onClick={handlePreviousPage}
                                                disabled={!hasPrevious || loading || isLoadingMore}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                Previous
                                            </button>
                                            <span className="text-muted small me-2">Page {currentPage}</span>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handleNextPage}
                                                disabled={!hasMore || loading || isLoadingMore}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                Next
                                            </button>
                                        </div>
                                        <div className="text-muted small">
                                            {totalCount > 0 && `Page ${currentPage} of approx. ${Math.ceil(totalCount / 25)}`}
                                            <span className="ms-2">25 actors per page</span>
                                        </div>
                                    </div>
                                )}

                                {/* Loading indicator */}
                                {isLoadingMore && (
                                    <div className="text-center py-3 border-top bg-light">
                                        <Spinner animation="border" size="sm" variant="primary" />
                                        <span className="ms-2 text-muted">Loading actors...</span>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
};

export default ThreatActorList; 
